import React, { useState, useEffect } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';

const PROVIDER_HELP = {
  permit_intel: 'Permit + property history network (nationwide).',
  property_data: 'Property facts — value, year built, last sale, type. Sharpens scoring.',
  skiptrace: 'Deep contact lookup provider (person-confirmed cell numbers).',
  numverify: 'Phone validation — carrier + line type.',
  credit: 'Soft-pull credit lender key (CRS / iSoftpull / Equifax style). Until added, pre-quals are clearly-marked SIMULATED placeholders and no real inquiry is made.',
  ai_openai: 'ChatGPT / OpenAI key (platform.openai.com). When present, ALL AI — assistant, roof vision, bill parsing, HD renders — runs on your OpenAI account instead of the bundled Phallox AI Engine.',
  ai_gemini: 'Google Gemini key (aistudio.google.com). Used for text + vision AI when no OpenAI key is set.',
  ai_anthropic: 'Anthropic Claude key (console.anthropic.com). Used for text AI when no OpenAI / Gemini key is set.',
};
const AI_POOLS = ['ai_openai', 'ai_gemini', 'ai_anthropic'];

export default function KeyManager() {
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState({});
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/keys');
      setPools(res.data.pools || []);
    } catch { toast.error('Failed to load keys (admin only)'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addKey = async (provider) => {
    const key = (newKey[provider] || '').trim();
    if (!key) return;
    setBusy(provider);
    try {
      await api.post('/api/admin/keys', { provider, key });
      setNewKey(p => ({ ...p, [provider]: '' }));
      toast.success('Key added to rotation');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(''); }
  };
  const removeKey = async (id) => {
    if (!window.confirm('Remove this key from rotation?')) return;
    try { await api.delete(`/api/admin/keys/${id}`); toast.success('Key removed'); load(); }
    catch { toast.error('Failed'); }
  };
  const resetBenched = async (provider) => {
    try { await api.post(`/api/admin/keys/reset/${provider}`); toast.success('Benched keys reactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="text-center py-16 text-text-muted"><i className="fa-solid fa-spinner fa-spin mr-2" />Loading key pools…</div>;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="key-manager">
      <div>
        <h3 className="font-heading text-base font-semibold text-text-primary mb-1">API Key Manager</h3>
        <p className="text-xs text-text-muted">Paste keys into a provider's rotation. When one key is exhausted, the next is used automatically. Keys are stored securely and never shown in full.</p>
      </div>
      {[['Data providers', pools.filter(p => !AI_POOLS.includes(p.provider))], ['AI engines — bring your own key (optional)', pools.filter(p => AI_POOLS.includes(p.provider))]].map(([title, list]) => (
      <div key={title} className="space-y-4" data-testid={`key-group-${title.startsWith('AI') ? 'ai' : 'data'}`}>
      <div className="pt-2"><p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{title}</p>
        {title.startsWith('AI') && <p className="text-[11px] text-text-muted mt-1">Without your own key, AI runs on the bundled <b>Phallox AI Engine</b>. Priority when several are set: ChatGPT → Gemini → Claude → Phallox.</p>}
      </div>
      {list.map(pool => (
        <div key={pool.provider} className="card-interactive p-5" data-testid={`key-pool-${pool.provider}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${pool.active_keys > 0 ? 'bg-primary' : 'bg-nardo-light'}`} />
              <h4 className="font-bold text-text-primary">{pool.label}</h4>
              <span className="text-[10px] font-mono text-text-muted">({pool.provider})</span>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary font-bold">{pool.active_keys} active</span>
              {pool.benched_keys > 0 && <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 font-bold">{pool.benched_keys} benched</span>}
              <span className="px-2 py-0.5 rounded-md bg-surface-alt text-text-muted">{pool.total_keys} total</span>
            </div>
          </div>
          <p className="text-[11px] text-text-muted mb-3">{PROVIDER_HELP[pool.provider]}</p>

          {pool.managed_keys?.length > 0 && (
            <div className="space-y-1.5 mb-3">
              {pool.managed_keys.map(k => (
                <div key={k.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-surface-alt border border-border" data-testid={`key-row-${k.id}`}>
                  <span className="text-xs font-mono text-text-primary">{k.masked}</span>
                  <button onClick={() => removeKey(k.id)} className="text-[11px] text-destructive hover:underline" data-testid={`remove-key-${k.id}`}>Remove</button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input type="password" value={newKey[pool.provider] || ''} onChange={e => setNewKey(p => ({ ...p, [pool.provider]: e.target.value }))}
              placeholder={`Paste a ${pool.label} key…`} className="input-field h-9 text-sm flex-1" data-testid={`key-input-${pool.provider}`} />
            <button onClick={() => addKey(pool.provider)} disabled={busy === pool.provider} className="btn-primary text-xs px-4" data-testid={`add-key-${pool.provider}`}>
              {busy === pool.provider ? 'Adding…' : 'Add'}
            </button>
            {pool.benched_keys > 0 && (
              <button onClick={() => resetBenched(pool.provider)} className="btn-secondary text-xs px-3" data-testid={`reset-${pool.provider}`}>Reactivate</button>
            )}
          </div>
        </div>
      ))}
      </div>
      ))}
    </div>
  );
}
